import Box from '@mui/material/Box';
import type { ReactElement } from 'react';
import { useTranslation } from 'react-i18next';

import { reasonMessage } from '../../i18n';

import type { DailyLimitValidation } from './daily-limit';
import type { RiskPreviewState } from './risk-preview-state';
import type { DraftIssue } from './settings-draft';

/**
 * What still blocks Save, under the button (M2 §4 "Form incomplete": the missing field is
 * named). A failed preview already says why saving is unavailable, so it adds nothing here.
 */
export function MissingList({
  issues,
  limitValidation,
  preview,
  previewCurrent,
  unchanged,
}: {
  readonly issues: readonly DraftIssue[];
  readonly limitValidation: DailyLimitValidation;
  readonly preview: RiskPreviewState;
  readonly previewCurrent: boolean;
  /** Everything is valid and equal to what is saved: a save would add an identical version. */
  readonly unchanged: boolean;
}): ReactElement | null {
  const { t } = useTranslation();
  const items: string[] = [];
  for (const issue of issues) {
    if (issue.field === 'fallbackStopLossPips' && issue.problem === 'required') {
      // C2 text of SETTINGS_FALLBACK_SL_REQUIRED (AC6).
      const message = reasonMessage(issue.reasonCode);
      items.push(t(message.key, message.values));
    } else if (issue.leadingDigit === true) {
      // ",5" stays incomplete (never read as 0.5, I7); the hint says what is missing.
      items.push(t('settings.errors.leadingDigit'));
    } else {
      items.push(t(`settings.errors.${issue.field}`));
    }
  }
  if (!limitValidation.ok) {
    items.push(
      t(
        limitValidation.leadingDigit === true
          ? 'settings.errors.leadingDigit'
          : 'settings.errors.dailyLoss',
      ),
    );
  }
  if (preview.status === 'loading' && !previewCurrent) {
    items.push(t('settings.errors.previewPending'));
  }
  if (unchanged) items.push(t('settings.errors.unchanged'));
  if (items.length === 0) return null;
  return (
    <Box sx={{ fontSize: 13, color: 'text.secondary' }}>
      {t('settings.missingTitle')}
      <Box component="ul" sx={{ m: 0, pl: 2.5 }}>
        {[...new Set(items)].map((item) => (
          <li key={item}>{item}</li>
        ))}
      </Box>
    </Box>
  );
}
